import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import logo from "assets/img/logo.png";
import {
  clienteService,
  ClientePublico,
  CampoExtra,
} from "services/clienteService";
import ModalConsentimiento from "./ModalConsentimiento";
import {
  reskillingService,
  ReskillingLookup,
  ReskillingSession,
  ReskillingStudentInput,
} from "services/reskillingService";

/** Cada cuánto se consulta si la evaluación ya terminó. */
const POLL_INTERVAL_MS = 15000;

const CAMPOS_OPCIONALES: Array<{
  name: keyof ReskillingStudentInput;
  label: string;
  type?: string;
}> = [
  { name: "phone", label: "Teléfono", type: "tel" },
  { name: "identification_number", label: "DNI" },
  { name: "city", label: "Ciudad" },
  { name: "occupation", label: "Ocupación" },
];

const ESTADO_INICIAL: ReskillingStudentInput = {
  email: "",
  first_name: "",
  last_name: "",
  phone: "",
  identification_type: "DNI",
  identification_number: "",
  country: "Argentina",
  city: "",
  occupation: "",
};

export default function EvaluacionLanding() {
  const [form, setForm] = useState<ReskillingStudentInput>(ESTADO_INICIAL);
  const [sesion, setSesion] = useState<ReskillingSession | null>(null);
  const [lookup, setLookup] = useState<ReskillingLookup | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** "nueva" pide todos los datos; "retomar" solo el email. */
  const [modo, setModo] = useState<"nueva" | "retomar">("nueva");
  const { slug } = useParams<{ slug?: string }>();
  const [cliente, setCliente] = useState<ClientePublico | null>(null);
  const [acepta, setAcepta] = useState(false);
  const [datosExtra, setDatosExtra] = useState<Record<string, string>>({});
  const [verConsentimiento, setVerConsentimiento] = useState(false);
  const [noEncontrado, setNoEncontrado] = useState(false);
  const [reenviando, setReenviando] = useState(false);
  const [avisoReenvio, setAvisoReenvio] = useState<string | null>(null);
  const [cargandoSesion, setCargandoSesion] = useState(
    () => new URLSearchParams(window.location.search).has("sesion")
  );
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Landing de un cliente: se carga su marca para mostrarla junto a la de
  // Neurografy. Sin slug, la evaluación es directa de Neurografy.
  useEffect(() => {
    if (!slug) return;

    let cancelado = false;

    clienteService
      .porSlug(slug)
      .then((c) => {
        if (!cancelado) setCliente(c);
      })
      .catch(() => {
        if (!cancelado) {
          setError("Esta evaluación no está disponible.");
        }
      });

    return () => {
      cancelado = true;
    };
  }, [slug]);

  // El email de continuación trae ?sesion=<uuid>: se carga esa sesión y la
  // persona retoma la evaluación acá, dentro del iframe.
  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("sesion");

    if (!sessionId) return;

    let cancelado = false;

    reskillingService
      .getSession(sessionId)
      .then((s) => {
        if (cancelado) return;

        setSesion(s);

        // Quien entra por el email no pasó por la landing, así que la marca
        // del cliente viene con la sesión.
        if (s.cliente) {
          setCliente({
            slug: s.cliente.slug,
            nombre: s.cliente.nombre,
            logo_url: s.cliente.logo_url,
            logo_secundario_url: s.cliente.logo_secundario_url,
            titulo: s.cliente.titulo,
            descripcion: null,
            color_principal: null,
            consentimiento: "",
          });
        }
      })
      .catch(() => {
        if (!cancelado) {
          setError(
            "No pudimos abrir esa evaluación. Completá tus datos para continuar."
          );
        }
      })
      .finally(() => {
        if (!cancelado) setCargandoSesion(false);
      });

    return () => {
      cancelado = true;
    };
  }, []);

  const detenerPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Mientras la evaluación esté en curso, se consulta periódicamente si terminó.
  useEffect(() => {
    if (!sesion || sesion.status === "completed") {
      detenerPolling();
      return;
    }

    pollRef.current = setInterval(async () => {
      try {
        const actualizada = await reskillingService.getSession(sesion.session_id);
        if (actualizada.status === "completed") {
          setSesion(actualizada);
        }
      } catch {
        // Silencioso: es un chequeo de fondo, no debe molestar al usuario.
      }
    }, POLL_INTERVAL_MS);

    return detenerPolling;
  }, [sesion, detenerPolling]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setEnviando(true);

    try {
      // Primero se consulta si este email ya tiene una evaluación, para no
      // arrancar de cero a alguien que dejó una a medias.
      const existente = await reskillingService.lookup(form.email, slug);

      if (existente.has_session) {
        setLookup(existente);
        return;
      }

      const creada = await reskillingService.createSession({
        ...form,
        cliente_slug: slug,
        consentimiento: true,
        datos_extra: datosExtra,
      });
      setSesion(creada);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "No se pudo iniciar la evaluación. Intentalo de nuevo."
      );
    } finally {
      setEnviando(false);
    }
  };

  /**
   * Busca una evaluación existente solo con el email, sin crear nada.
   * Para quien ya la empezó y no quiere volver a cargar todos sus datos.
   */
  const handleRetomar = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setNoEncontrado(false);
    setEnviando(true);

    try {
      const existente = await reskillingService.lookup(form.email, slug);

      if (existente.has_session) {
        setLookup(existente);
      } else {
        setNoEncontrado(true);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "No pudimos buscar tu evaluación. Intentalo de nuevo."
      );
    } finally {
      setEnviando(false);
    }
  };

  const handleResend = async () => {
    if (!sesion) return;

    setReenviando(true);
    setAvisoReenvio(null);

    try {
      await reskillingService.resendLink(sesion.session_id);
      setAvisoReenvio("¡Listo! Revisá tu correo.");
    } catch {
      setAvisoReenvio("No se pudo reenviar. Probá de nuevo en un rato.");
    } finally {
      setReenviando(false);
    }
  };

  // --- Cargando la sesión que viene del email -------------------------------
  if (cargandoSesion) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100 px-4">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-brand-500"></div>
          <p className="text-lg font-semibold text-gray-700">
            Abriendo tu evaluación…
          </p>
        </div>
      </div>
    );
  }

  // --- Ya tenía una evaluación (en curso o completada) ----------------------
  if (lookup?.has_session) {
    const yaCompletada = lookup.status === "completed";

    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100 px-4 py-8">
        <div className="w-full max-w-md rounded-lg bg-white p-6 text-center shadow-lg">
          <Logo cliente={cliente} />

          <div
            className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full ${
              yaCompletada ? "bg-green-100" : "bg-blue-100"
            }`}
          >
            <svg
              className={`h-8 w-8 ${
                yaCompletada ? "text-green-500" : "text-blue-500"
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={
                  yaCompletada
                    ? "M5 13l4 4L19 7"
                    : "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                }
              />
            </svg>
          </div>

          {yaCompletada ? (
            <>
              <h2 className="mb-2 text-xl font-bold text-gray-800 md:text-2xl">
                Ya completaste tu evaluación
              </h2>
              <p className="text-sm text-gray-600 md:text-base">
                Registramos una evaluación finalizada para{" "}
                <strong>{form.email}</strong>. No hace falta que la vuelvas a
                hacer.
              </p>
            </>
          ) : (
            <>
              <h2 className="mb-2 text-xl font-bold text-gray-800 md:text-2xl">
                Ya tenés una evaluación empezada
              </h2>
              {/*
                El link de continuación llega solo por email, nunca desde acá:
                de lo contrario, conocer un email alcanzaría para seguir la
                evaluación de otra persona.
              */}
              <p className="text-sm text-gray-600 md:text-base">
                Te enviamos un email a <strong>{form.email}</strong> con el link
                para continuarla desde donde la dejaste. Tus respuestas están
                guardadas.
              </p>
              <p className="mt-3 text-xs text-gray-500">
                Revisá tu bandeja de entrada y la carpeta de spam.
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  // --- Evaluación terminada -------------------------------------------------
  if (sesion?.status === "completed") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100 px-4 py-8">
        <div className="w-full max-w-md rounded-lg bg-white p-6 text-center shadow-lg">
          <Logo cliente={cliente} />
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <svg
              className="h-8 w-8 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="mb-2 text-xl font-bold text-gray-800 md:text-2xl">
            ¡Evaluación completada!
          </h2>
          <p className="text-sm text-gray-600 md:text-base">
            Gracias por completar la evaluación
            {form.first_name ? `, ${form.first_name}` : ""}. Ya recibimos tus
            resultados.
          </p>
        </div>
      </div>
    );
  }

  // --- Evaluación en curso: iframe -----------------------------------------
  if (sesion?.iframe_url) {
    return (
      <div className="flex min-h-screen flex-col bg-gray-100">
        <div className="flex flex-wrap items-center gap-3 border-b bg-white px-4 py-3 shadow-sm">
          <LogosBarra cliente={cliente} />
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold text-gray-800 md:text-lg">
              {cliente?.titulo || "Evaluación de habilidades"}
            </h1>
            <p className="text-xs text-gray-500 md:text-sm">
              Completá la evaluación en el recuadro. Puede tardar unos minutos.
            </p>
          </div>
        </div>

        {/* allow="microphone" es obligatorio: la evaluación graba audio. */}
        <iframe
          src={sesion.iframe_url}
          title="Evaluación de habilidades"
          className="w-full flex-1 border-0"
          allow="microphone; fullscreen"
        />

        <div className="border-t bg-white px-4 py-2 text-center text-xs text-gray-500 md:text-sm">
          <p>
            {form.email ? (
              <>
                Te enviamos el link a <strong>{form.email}</strong> para que
                puedas retomarla cuando quieras.{" "}
              </>
            ) : (
              <>Podés retomar esta evaluación desde el link de tu email. </>
            )}
            <button
              type="button"
              onClick={handleResend}
              disabled={reenviando}
              className="text-brand-500 hover:underline disabled:opacity-60"
            >
              {reenviando ? "Enviando…" : "Reenviar email"}
            </button>
            {avisoReenvio && (
              <span className="ml-2 text-green-600">{avisoReenvio}</span>
            )}
          </p>
        </div>
      </div>
    );
  }

  // --- Retomar una evaluación: solo el email --------------------------------
  if (modo === "retomar") {
    return (
      <div className="min-h-screen bg-gray-100 px-4 py-8">
        <div className="mx-auto w-full max-w-lg rounded-lg bg-white p-6 shadow-lg">
          <Logo cliente={cliente} />
          <h1 className="mb-2 text-xl font-bold text-gray-800 md:text-2xl">
            Retomá tu evaluación
          </h1>
          <p className="mb-6 text-sm text-gray-600 md:text-base">
            Ingresá el email con el que la empezaste y te enviamos el link para
            continuar desde donde la dejaste.
          </p>

          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {noEncontrado && (
            <div className="mb-4 rounded-md bg-amber-50 p-3 text-sm text-amber-800">
              No encontramos ninguna evaluación con ese email.{" "}
              <button
                type="button"
                onClick={() => {
                  setModo("nueva");
                  setNoEncontrado(false);
                }}
                className="font-medium underline"
              >
                Empezá una nueva
              </button>
              .
            </div>
          )}

          <form onSubmit={handleRetomar} className="space-y-4">
            <Campo
              name="email"
              label="Email"
              type="email"
              value={form.email}
              onChange={handleChange}
              required
            />

            <button
              type="submit"
              disabled={enviando}
              className="w-full rounded-md bg-brand-500 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {enviando ? "Buscando…" : "Buscar mi evaluación"}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-600">
            ¿Todavía no empezaste?{" "}
            <button
              type="button"
              onClick={() => {
                setModo("nueva");
                setError(null);
                setNoEncontrado(false);
              }}
              className="font-medium text-brand-500 hover:underline"
            >
              Comenzar una evaluación
            </button>
          </p>
        </div>
      </div>
    );
  }

  // --- Formulario inicial ---------------------------------------------------
  return (
    <div className="min-h-screen bg-gray-100 px-4 py-8">
      <div className="mx-auto w-full max-w-lg rounded-lg bg-white p-6 shadow-lg">
        <Logo cliente={cliente} />
        <h1 className="mb-2 text-xl font-bold text-gray-800 md:text-2xl">
          {cliente?.titulo || "Evaluación de habilidades"}
        </h1>
        <p className="mb-6 text-sm text-gray-600 md:text-base">
          {cliente?.descripcion ||
            "Completá tus datos para comenzar. La evaluación toma alrededor de 20 minutos, e incluye preguntas con respuesta por voz. Te enviaremos el link por email para que puedas retomarla si no llegás a terminarla."}
        </p>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Campo
              name="first_name"
              label="Nombre"
              value={form.first_name}
              onChange={handleChange}
              required
            />
            <Campo
              name="last_name"
              label="Apellido"
              value={form.last_name}
              onChange={handleChange}
              required
            />
          </div>

          <Campo
            name="email"
            label="Email"
            type="email"
            value={form.email}
            onChange={handleChange}
            required
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {CAMPOS_OPCIONALES.filter(
              (c) => !(cliente?.campos_ocultos ?? []).includes(c.name)
            ).map(({ name, label, type }) => (
              <Campo
                key={name}
                name={name}
                label={label}
                type={type}
                value={(form[name] as string) || ""}
                onChange={handleChange}
              />
            ))}

            {/* Campos que pide este cliente en particular. */}
            {(cliente?.campos_extra ?? []).map((campo) => (
              <CampoExtraForm
                key={campo.clave}
                campo={campo}
                valor={datosExtra[campo.clave] ?? ""}
                onChange={(v) =>
                  setDatosExtra((prev) => ({ ...prev, [campo.clave]: v }))
                }
              />
            ))}
          </div>

          <label className="flex items-start gap-2 rounded-md bg-gray-50 p-3 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={acepta}
              onChange={(e) => setAcepta(e.target.checked)}
              className="mt-0.5 shrink-0"
            />
            <span>
              Acepto participar en la evaluación y he leído el{" "}
              <button
                type="button"
                onClick={() => setVerConsentimiento(true)}
                className="font-medium text-brand-500 underline"
              >
                consentimiento informado
              </button>
              .
            </span>
          </label>

          <button
            type="submit"
            disabled={enviando || !acepta}
            className="w-full rounded-md bg-brand-500 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {enviando ? "Preparando tu evaluación…" : "Comenzar evaluación"}
          </button>
        </form>

        {verConsentimiento && (
          <ModalConsentimiento
            texto={cliente?.consentimiento ?? ""}
            onCerrar={() => setVerConsentimiento(false)}
          />
        )}

        <p className="mt-6 border-t pt-4 text-center text-sm text-gray-600">
          ¿Ya empezaste tu evaluación?{" "}
          <button
            type="button"
            onClick={() => {
              setModo("retomar");
              setError(null);
            }}
            className="font-medium text-brand-500 hover:underline"
          >
            Continuá con tu email
          </button>
        </p>
      </div>
    </div>
  );
}

/** Logos en la barra del iframe: más chicos, alineados a la izquierda. */
function LogosBarra({ cliente }: { cliente?: ClientePublico | null }) {
  const logosCliente = [cliente?.logo_url, cliente?.logo_secundario_url].filter(
    Boolean
  ) as string[];

  return (
    <div className="flex shrink-0 items-center gap-3">
      <img src={logo} alt="Neurografy" className="h-9 w-auto" />
      {logosCliente.length > 0 && (
        <span className="h-8 w-px bg-gray-200" />
      )}
      {logosCliente.map((url) => (
        <img
          key={url}
          src={url}
          alt={cliente?.nombre ?? ""}
          className="h-9 w-auto"
        />
      ))}
    </div>
  );
}

function Logo({ cliente }: { cliente?: ClientePublico | null }) {
  // Cuando la evaluación es de un cliente, sus logos acompañan al de
  // Neurografy. Algunas instituciones se presentan con más de una marca.
  const logosCliente = [cliente?.logo_url, cliente?.logo_secundario_url].filter(
    Boolean
  ) as string[];

  if (logosCliente.length > 0) {
    return (
      <div className="mb-6 flex flex-wrap items-center justify-center gap-4">
        <img src={logo} alt="Neurografy" className="h-11 w-auto md:h-12" />
        <span className="hidden h-10 w-px bg-gray-200 sm:block" />
        {logosCliente.map((url) => (
          <img
            key={url}
            src={url}
            alt={cliente?.nombre ?? ""}
            className="h-11 w-auto md:h-12"
          />
        ))}
      </div>
    );
  }

  return (
    <img
      src={logo}
      alt="Neurografy"
      className="mx-auto mb-6 h-16 w-auto md:h-20"
    />
  );
}

/** Un campo adicional definido por el cliente: texto o lista de opciones. */
function CampoExtraForm({
  campo,
  valor,
  onChange,
}: {
  campo: CampoExtra;
  valor: string;
  onChange: (v: string) => void;
}) {
  const id = `extra-${campo.clave}`;
  const clases =
    "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none";

  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-gray-700">
        {campo.etiqueta}
        {campo.obligatorio && <span className="text-red-500"> *</span>}
      </label>

      {campo.tipo === "opciones" ? (
        <select
          id={id}
          value={valor}
          onChange={(e) => onChange(e.target.value)}
          required={campo.obligatorio}
          className={clases}
        >
          <option value="">Seleccioná una opción</option>
          {(campo.opciones ?? []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={id}
          type="text"
          value={valor}
          onChange={(e) => onChange(e.target.value)}
          required={campo.obligatorio}
          className={clases}
        />
      )}
    </div>
  );
}

interface CampoProps {
  name: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  required?: boolean;
}

function Campo({ name, label, value, onChange, type = "text", required }: CampoProps) {
  return (
    <div>
      <label htmlFor={name} className="mb-1 block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
      />
    </div>
  );
}
